/**
 * The message list (the prototype's `messageList()`): heading with the view's name, count and order,
 * then the rows — virtualised with @tanstack/react-virtual (only the visible rows plus a small overscan
 * are in the DOM; the next page loads near the end) — and the footer "N unread · M min to read"
 * (fix 9: minutes of UNREAD messages only, from the engine's `unreadMinutes`).
 *
 * Rows are keyed by message id, so a background update (new messages, a sync) neither loses the
 * selection nor moves the scroll position. The scroll offset is remembered per scope (Back restores it).
 */

import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import type { MessageId } from '@digester/core';
import { Count } from '../ui/Count.js';
import { formatMinutes } from '../ui/format.js';
import type { Lookups, ReaderList } from './data.js';
import { MessageRowView } from './MessageRow.js';
import { recallScroll, rememberScroll } from './scroll-memory.js';

export interface MessageListProps {
  title: string;
  list: ReaderList;
  selectedId: MessageId | null;
  lookups: Lookups;
  showImages: boolean;
  timeZone: string | undefined;
  orderLabel: string;
  /** The heading's tool (Edit view, or Briefing on phones). */
  headingTool: ReactNode;
  /** Remembered-scroll key of this list (scope). */
  scrollKey: string;
  /** Changes when the user moved the selection (J/K, a click): the row is scrolled into view. */
  selectionTick: number;
  empty: { title: string; canClear: boolean; onClear(): void };
  gone?: Set<string>;
  onOpen(id: MessageId): void;
}

const ESTIMATE = 176;
/** Load the next page this many rows before the end. */
const PREFETCH_ROWS = 15;

export function MessageList({ title, list, selectedId, lookups, showImages, timeZone, orderLabel, headingTool, scrollKey, selectionTick, empty, gone, onOpen }: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = list.rows;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATE,
    overscan: 6,
    getItemKey: (index) => rows[index]?.id ?? index,
    initialOffset: () => recallScroll(scrollKey, 'list') ?? 0,
  });
  const items = virtualizer.getVirtualItems();

  // Scroll anchoring: when rows are added or removed above the viewport (a harvest, a sync, a move),
  // the first visible row stays where it was on screen. At the very top, new rows simply appear.
  const anchor = useRef<{ id: string; offset: number } | null>(null);
  const captureAnchor = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop;
    // From the measurements, not getVirtualItems(): those follow the scroll event and can be stale here.
    const items = virtualizer.measurementsCache;
    let lo = 0;
    let hi = items.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((items[mid]?.end ?? 0) > top) hi = mid;
      else lo = mid + 1;
    }
    const first = items[lo];
    const row = first ? rows[first.index] : undefined;
    anchor.current = top > 0 && first && row ? { id: row.id, offset: first.start - top } : null;
  };
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const a = anchor.current;
    if (el && a) {
      const index = rows.findIndex((r) => r.id === a.id);
      const start = index >= 0 ? virtualizer.measurementsCache[index]?.start : undefined;
      if (start !== undefined && Math.abs(start - a.offset - el.scrollTop) > 1) virtualizer.scrollToOffset(start - a.offset);
    }
    captureAnchor();
    // Only a change of the rows themselves re-anchors.
  }, [rows]);

  // Remember the scroll offset of this scope.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        rememberScroll(scrollKey, 'list', el.scrollTop);
        captureAnchor();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('scroll', onScroll);
    };
  }, [scrollKey]);

  // A different scope starts at its remembered offset (or the top).
  const lastScope = useRef(scrollKey);
  useLayoutEffect(() => {
    if (lastScope.current === scrollKey) return;
    lastScope.current = scrollKey;
    virtualizer.scrollToOffset(recallScroll(scrollKey, 'list') ?? 0);
  }, [scrollKey, virtualizer]);

  // Keep the selected row visible when the user moves the selection.
  const lastTick = useRef(selectionTick);
  useEffect(() => {
    if (lastTick.current === selectionTick) return;
    lastTick.current = selectionTick;
    const index = rows.findIndex((r) => r.id === selectedId);
    if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' });
  }, [selectionTick, selectedId, rows, virtualizer]);

  // Infinite loading near the end.
  const lastIndex = items[items.length - 1]?.index ?? -1;
  useEffect(() => {
    if (lastIndex >= rows.length - PREFETCH_ROWS && list.hasNextPage && !list.fetchingNext) list.fetchNextPage();
  }, [lastIndex, rows.length, list]);

  return (
    <section className="message-list" aria-label={`${title} messages`}>
      <div className="pane-heading">
        <span>
          {title} <Count value={list.total} noun="messages" />
        </span>
        <span className="list-heading-actions">
          <span className="list-order">{orderLabel}</span>
          {headingTool}
        </span>
      </div>
      <div className="message-items" ref={scrollRef} role="feed" aria-busy={list.loading || list.fetchingNext}>
        {rows.length ? (
          <div className="message-items-inner" style={{ height: virtualizer.getTotalSize() }}>
            {items.map((item) => {
              const row = rows[item.index];
              if (!row) return null;
              return (
                <div key={item.key} className="message-slot" data-index={item.index} ref={virtualizer.measureElement} style={{ transform: `translateY(${item.start}px)` }}>
                  <MessageRowView row={row} selected={row.id === selectedId} lookups={lookups} showImages={showImages} timeZone={timeZone} gone={gone?.has(row.id) ?? false} onOpen={onOpen} />
                </div>
              );
            })}
          </div>
        ) : list.loading ? null : list.error ? (
          <div className="empty" role="alert">
            <h2>Messages could not be loaded</h2>
            <p className="subtle">{list.error.message}</p>
          </div>
        ) : (
          <div className="empty">
            <h2>{empty.title}</h2>
            {empty.canClear ? (
              <button type="button" onClick={empty.onClear}>
                Clear filters
              </button>
            ) : null}
          </div>
        )}
      </div>
      <div className="pane-footer">
        <span>
          <Count value={list.unread} as="span" noun="unread messages" /> unread
        </span>
        <span>{formatMinutes(list.unreadMinutes)} to read</span>
      </div>
    </section>
  );
}
